import { useMutation, useQueryClient } from '@tanstack/react-query';
import { METHOD, sendRequest } from '@/state/http-request.helpers.ts';
import { useAuth } from '@clerk/clerk-react';
import { GET_MEDIA_QUERY_KEY } from '@/state/queries/media/use-get-media.ts';
import type { DeleteMediaDto } from '@/types/dtos/media/request/delete-media.dto.ts';
import { GET_EVENT_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-event-sessions.ts';
import { GET_EVENT_FILTERED_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-filtered-event-sessions.ts';
import { GET_EVENT_FLOWS_QUERY_KEY } from '@/state/queries/flows/use-get-event-flows.ts';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow.ts';
import { GET_SESSION_QUERY_KEY } from '@/state/queries/sessions/use-get-session.ts';
import { GET_CONTRIBUTOR_SUMMARY_QUERY_KEY } from '@/state/queries/contributors/user-contributors.ts';
import { invalidateSimulationCueSheet } from '@/state/queries/player/use-get-simulation-cue-sheet';

export const useDeleteMediaMutation = () => {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (deleteMediaDto: DeleteMediaDto) => {
      const token = await getToken();
      return deleteMedia(deleteMediaDto, token ?? '');
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: [GET_MEDIA_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_SESSIONS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_FILTERED_SESSIONS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_FLOWS_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_FLOW_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_SESSION_QUERY_KEY] }),
        queryClient.invalidateQueries({ queryKey: [GET_CONTRIBUTOR_SUMMARY_QUERY_KEY] }),
        invalidateSimulationCueSheet(queryClient),
      ]);
    },
  });
};

const deleteMedia = async (deleteMediaDto: DeleteMediaDto, token: string) => {
  const url = `${import.meta.env.VITE_API_URL}/media/${deleteMediaDto.mediaId}`;
  return sendRequest({ url, method: METHOD.DELETE, token });
};
